import { useEffect, useState } from "react";
import { formatCode, getShare } from "./shareApi";

/**
 * 공유받은 분류 결과(2026-10-06) — /s/토큰. 로그인 없이, 앱 셸 없이 한 장 요약. 휴대폰 화면에 맞춘다.
 * 품명·세율은 서버가 관세율표에서 다시 채운 값이다. 확정이 아니라는 문구와 loginHS 로 가는 길을 함께 둔다.
 */
const C = { ink: "#1b1f27", muted: "#6b7482", line: "#e4e7ec", soft: "#f5f6f8", blue: "#1a56db" };

export default function SharedResultPage({ token, fetchImpl = fetch }) {
  const [state, setState] = useState({ status: "loading" });
  useEffect(() => {
    document.title = "LoginHS — 공유된 분류 결과";
    getShare(token, fetchImpl)
      .then((j) => setState({ status: "ok", data: j }))
      .catch((e) => setState({ status: e.status === 410 ? "gone" : "missing", message: e.message }));
  }, [token, fetchImpl]);

  const wrap = (children) => (
    <div style={{ minHeight: "100vh", background: C.soft, padding: "24px 16px", boxSizing: "border-box", fontFamily: "'Malgun Gothic', 'Pretendard', system-ui, sans-serif", color: C.ink }}>
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <a href="/" style={{ display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none", color: C.ink, fontWeight: 800, fontSize: 17, marginBottom: 14 }}>
          <img src="/pwa/icon-loginhs-192.png?v=20261002" alt="" width={24} height={24} style={{ borderRadius: 6 }} /> LoginHS
        </a>
        {children}
      </div>
    </div>
  );
  if (state.status === "loading") return wrap(<p style={{ color: C.muted }}>불러오는 중…</p>);
  if (state.status !== "ok") {
    return wrap(
      <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 12, padding: 20 }} data-testid="share-gone">
        <b>{state.status === "gone" ? "공유가 끝났습니다" : "공유를 찾을 수 없습니다"}</b>
        <p style={{ color: C.muted, margin: "6px 0 14px", fontSize: 14 }}>{state.message}</p>
        <a href="/" style={{ color: C.blue }}>LoginHS 에서 직접 찾아보기 →</a>
      </div>,
    );
  }
  const d = state.data;
  const p = d.payload || {};
  const r = p.rates || {};
  const searchHref = p.query ? `/?audit=onto-detail&sq=${encodeURIComponent(p.query)}` : `/?audit=quickref&noextra=1&qhs=${String(p.code).slice(0, 4)}&hsk=${p.code}`;
  return wrap(
    <article data-testid="shared-result" style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden" }}>
      <div style={{ padding: "18px 20px", borderBottom: `1px solid ${C.line}` }}>
        <div style={{ fontSize: 12, color: C.muted }}>공유된 세번 분류 결과 · {String(d.created_at).slice(0, 10)}</div>
        {p.query && <div style={{ fontSize: 15, marginTop: 6 }}>검색어 <b>{p.query}</b></div>}
        {p.code && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontFamily: "Consolas, monospace", fontSize: 26, fontWeight: 800, color: C.blue, letterSpacing: "-0.5px" }}>{formatCode(p.code)}</div>
            {p.name_ko && <div style={{ fontSize: 14, marginTop: 2 }}>{p.name_ko}</div>}
            {p.name_en && <div style={{ fontSize: 12, color: C.muted }}>{p.name_en}</div>}
          </div>
        )}
        {(r.base || r.wto || r.fta) && (
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10, fontSize: 13 }}>
            {r.base && <span><span style={{ color: C.muted }}>기본</span> <b>{r.base}</b></span>}
            {r.wto && <span><span style={{ color: C.muted }}>WTO</span> <b>{r.wto}</b></span>}
            {r.fta && <span><span style={{ color: C.muted }}>FTA 최저</span> <b>{r.fta}</b></span>}
          </div>
        )}
        {p.note && <div style={{ marginTop: 12, padding: "10px 12px", background: C.soft, borderRadius: 8, fontSize: 14 }}>{p.note}</div>}
      </div>
      {(p.sources || []).length > 0 && (
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.line}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>출처별 세번 {p.agreement && <span style={{ fontWeight: 400, color: C.muted }}>· {p.agreement}</span>}</div>
          {p.sources.map((s, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "84px 1fr", gap: 8, padding: "6px 0", borderTop: i ? `1px solid ${C.line}` : 0, fontSize: 13 }}>
              <span style={{ color: C.muted }}>{s.label}</span>
              <span>
                <b style={{ fontFamily: "Consolas, monospace" }}>{formatCode(s.code)}</b>{s.agree && <span style={{ fontSize: 11, color: C.blue, marginLeft: 6 }}>일치</span>}
                {s.detail && <span style={{ display: "block", color: C.muted, fontSize: 12 }}>{s.detail}</span>}
              </span>
            </div>
          ))}
        </div>
      )}
      {(p.checks || []).length > 0 && (
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.line}`, fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>확인할 점</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>{p.checks.map((c) => <li key={c}>{c}</li>)}</ul>
        </div>
      )}
      <div style={{ padding: "14px 20px", display: "grid", gap: 10 }}>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>LoginHS 의 제안이며 확정된 분류가 아닙니다. 신고 전 관세율표·결정례 원문을 확인하세요. 이 링크는 {String(d.expires_at).slice(0, 10)} 까지 열립니다.</div>
        <a href={searchHref} style={{ display: "inline-block", textAlign: "center", background: C.blue, color: "#fff", textDecoration: "none", padding: "11px 16px", borderRadius: 9, fontWeight: 700 }}>LoginHS 에서 근거 자세히 보기</a>
      </div>
    </article>,
  );
}
